import { useRef, useState } from 'react'
import './FileDropZone.css'

/**
 * One drop zone, shared by every upload surface on the platform.
 *
 * Winnie, 11 Sep: "every 'Upload bills' / 'Upload an invoice' feature across the platform
 * gets the same drag-and-drop capability, not just this one." Copying a drop handler into
 * five components is how five of them end up behaving slightly differently a year later,
 * so the zone lives here and each surface passes what it accepts.
 *
 * Manual selection is kept rather than replaced. Dragging is an addition for the people
 * who prefer it; the button is still there for the people who do not, and for anyone
 * driving the page from a keyboard.
 *
 * File type and size limits are not this component's business. They belong to the surface
 * that knows what it is collecting, and they are unchanged by the drag-and-drop addition.
 */
export interface FileDropZoneProps {
  onFiles: (files: File[]) => void
  /** Passed straight through to the input, so each surface keeps its own list. */
  accept?: string
  multiple?: boolean
  disabled?: boolean
  /** What the button says. The drag line above it is the same everywhere. */
  label?: string
  /** A quiet line under the button — the size and type limits this surface already had. */
  hint?: string
  busy?: boolean
  busyLabel?: string
}

export default function FileDropZone({
  onFiles,
  accept,
  multiple = false,
  disabled = false,
  label = 'Select files',
  hint,
  busy = false,
  busyLabel = 'Reading…',
}: FileDropZoneProps) {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [over, setOver] = useState(false)
  /*
   * Winnie, 10 Oct: "Should clearly show that the file has been uploaded."
   *
   * Choosing a file used to leave the zone looking exactly as it did before, so there
   * was no telling whether anything had been taken. The zone now names what it was
   * given, under the button, on every surface that uses it. It says "uploading" while
   * the surface is busy and "attached" once it is not; a surface that refuses the file
   * still shows its own error, as it always has.
   */
  const [picked, setPicked] = useState<string[]>([])

  const locked = disabled || busy

  const hand = (list: FileList | null) => {
    const files = list ? Array.from(list) : []
    if (!files.length) return
    /* A single-file surface takes the first one rather than silently dropping the lot. */
    const chosen = multiple ? files : [files[0]]
    setPicked(chosen.map((f) => f.name))
    onFiles(chosen)
  }

  return (
    <div
      className={`fdz${over ? ' is-over' : ''}${locked ? ' is-locked' : ''}`}
      onDragOver={(e) => {
        if (locked) return
        e.preventDefault()
        setOver(true)
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        if (locked) return
        e.preventDefault()
        setOver(false)
        hand(e.dataTransfer ? e.dataTransfer.files : null)
      }}
    >
      <p className="fdz-prompt">
        {busy
          ? busyLabel
          : multiple
          ? 'Drag and drop file(s) or select manually'
          : 'Drag and drop a file or select manually'}
      </p>

      <button
        type="button"
        className="fdz-btn"
        disabled={locked}
        onClick={() => inputRef.current && inputRef.current.click()}
      >
        {label}
      </button>

      {hint ? <p className="fdz-hint">{hint}</p> : null}

      {picked.length ? (
        <ul className="fdz-picked" aria-live="polite">
          {picked.map((name, i) => (
            <li key={name + i} className={busy ? 'is-busy' : 'is-done'}>
              <span className="fdz-tick" aria-hidden="true">{busy ? '…' : '✓'}</span>
              <span className="fdz-name">{name}</span>
              <span className="fdz-state">{busy ? 'uploading' : 'attached'}</span>
            </li>
          ))}
        </ul>
      ) : null}

      <input
        ref={inputRef}
        className="fdz-input"
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={locked}
        onChange={(e) => {
          hand(e.target.files)
          /* Cleared, so choosing the same file twice in a row still fires a change. */
          e.target.value = ''
        }}
      />
    </div>
  )
}
